import { zustandImmerMiddleware } from '@sketchnote/shared-components';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { useTimerWidgetServices } from './useTimerWidgetServices';

export interface Timer {
  billable: boolean;
  board: string;
  board_title: string;
  createdAt: string;
  file: string;
  filename: string;
  isRunning: boolean;
  member: string;
  startDate: string;
  startTime: string;
  status: string;
  stopTime?: string;
  task: any;
  task_title: string;
  task_type: string;
  updatedAt: string;
  _id: string;
  offset?: { hours: string; minutes: string; seconds: string };
}

interface TimerWidgetStore {
  timers: Timer[];
  isLoading: boolean;
  isRunningTimersModalOpen: boolean;
  setIsRunningTimersModalOpen: (isOpen: boolean) => void;
  isMinimized: boolean;
  minimize: () => void;
  maximize: () => void;
  fetchTimers: () => void;
  startTimer: (taskId: string) => void;
  resumeTimer: (taskId: string, startTime: string) => void;
  stopTimer: (taskId: string, startTime: string, isBillable: boolean) => void;
  pauseTimer: (taskId: string, startTime: string, isBillable: boolean) => void;
  addTimer: (timer: Timer) => void;
  selectedTimerIndex: number;
  setSelectedTimerIndex: (index: number) => void;
  position: { x: number; y: number };
  setPosition: (position: { x: number; y: number }) => void;
}

export const useTimerWidgetStore = create<
  TimerWidgetStore,
  [['zustand/devtools', TimerWidgetStore], ['zustand/immer', TimerWidgetStore]]
>(
  devtools(
    zustandImmerMiddleware((set, get) => ({
      isLoading: false,
      isRunningTimersModalOpen: false,
      isMinimized: false,
      timers: [],
      selectedTimerIndex: 0,
      position: { x: 0, y: 0 },
      setIsRunningTimersModalOpen: (isOpen: boolean) =>
        set(() => ({ isRunningTimersModalOpen: isOpen })),
      minimize: () => set(() => ({ isMinimized: true })),
      maximize: () => set(() => ({ isMinimized: false })),
      fetchTimers: async () => {
        set((s) => {
          s.isLoading = true;
        });
        const response = await useTimerWidgetServices().fetchTimer();
        set((s) => {
          s.timers = response || [];
          s.isLoading = false;
        });
      },
      startTimer: async (taskId: string) => {
        set((s) => {
          s.isLoading = true;
        });
        const response = await useTimerWidgetServices().startTimer(taskId);
        if (response) {
          get().fetchTimers();
        }
      },
      resumeTimer: async (taskId: string, startTime: string) => {
        set((s) => {
          s.isLoading = true;
        });
        const response = await useTimerWidgetServices().resumeTimer(taskId, startTime);
        if (response) {
          get().fetchTimers();
        }
      },
      stopTimer: async (taskId: string, startTime: string, isBillable: boolean) => {
        set((s) => {
          s.isLoading = true;
          s.selectedTimerIndex = 0;
        });
        const response = await useTimerWidgetServices().stopTimer(taskId, startTime, isBillable);
        if (response) {
          get().fetchTimers();
        }
      },
      pauseTimer: async (taskId: string, startTime: string, isBillable: boolean) => {
        set((s) => {
          s.isLoading = true;
          s.selectedTimerIndex = 0;
        });
        const response = await useTimerWidgetServices().pauseTimer(taskId, startTime, isBillable);
        if (response) {
          get().fetchTimers();
        }
      },
      addTimer: (timer: Timer) => {
        set((s) => {
          s.timers.unshift(timer);
        });
      },
      setSelectedTimerIndex: (index: number) => {
        set(() => ({ selectedTimerIndex: index }));
      },
      setPosition: (position: { x: number; y: number }) => {
        set(() => ({ position }));
      },
    }))
  )
);
